import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck, Phone, Check, X, Clock } from "lucide-react";

const STATUS = {
  requested: { label: "New", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  declined: { label: "Declined", cls: "bg-gray-100 text-gray-500 border-gray-200" },
};

export default function VisitRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/visit-requests/mine").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/visit-requests/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Visit confirmed — tenant notified" : "Declined — tenant notified");
      load();
    } catch { toast.error("Couldn't update"); }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-brand-subtle p-5" data-testid="visit-requests-panel">
      <div className="flex items-center gap-2 mb-4">
        <CalendarCheck size={18} className="text-[#C9A24A]" />
        <h3 className="font-display text-xl brand-forest">Visit requests</h3>
        <span className="text-xs text-gray-400">({items.length})</span>
      </div>
      <div className="space-y-3">
        {items.map((v) => {
          const s = STATUS[v.status] || STATUS.requested;
          return (
            <div key={v.id} className="rounded-xl border border-brand-subtle p-3" data-testid="visit-request-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/property/${v.property_id}`} className="font-semibold text-sm brand-forest hover:underline line-clamp-1">{v.property_title}</Link>
                  <p className="text-sm text-gray-800 mt-0.5">{v.tenant_name}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                    <span className="inline-flex items-center gap-1"><Phone size={11} /> {v.tenant_phone}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {v.slot}</span>
                  </div>
                  {v.note && <p className="text-xs text-gray-500 mt-1 italic">"{v.note}"</p>}
                </div>
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
              </div>
              {v.status === "requested" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setStatus(v.id, "confirmed")} data-testid="visit-confirm-btn"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"><Check size={13} /> Confirm</button>
                  <button onClick={() => setStatus(v.id, "declined")} data-testid="visit-decline-btn"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"><X size={13} /> Decline</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
